
import { CompositeFilterDescriptor, State } from "@progress/kendo-data-query";
import { Button } from "@progress/kendo-react-buttons";
import { PagerTargetEvent } from "@progress/kendo-react-data-tools";
import { ExcelExport, ExcelExportColumn } from "@progress/kendo-react-excel-export";
import { Grid, GridColumn, GridDataStateChangeEvent, GridFilterChangeEvent, GridPageChangeEvent, GridSortChangeEvent } from "@progress/kendo-react-grid";
import { Drawer, DrawerContent } from "@progress/kendo-react-layout";
import { X } from "lucide-react";
import { useRef, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ColumnMenu } from "../../component/global/ColumnMenu";
import Export from "../../component/global/Export";
import { IAuditLog } from "../../interfaces/auditLog.interface";
import IPageState from "../../interfaces/page.interface";
import auditLogSvc from "../../services/auditLog.service";
import { DateCell } from "../../shared-components/custom-cells";
import { getPaginationData } from "../../utils/utils";

const formatDateForExport = (value: string | null | undefined): string => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "pm" : "am";
  hours = hours % 12 || 12;
  return `${day}/${month}/${year} ${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
};

interface IProjectSummary {
  projectId?: string;
  projectName?: string;
  text?: string;
}

interface IProps {
  showAuditLogsDrawer: boolean;
  setShowAuditLogsDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  selectedProject: IProjectSummary | null;
}

function ProjectAuditLogsDrawer({
  showAuditLogsDrawer,
  setShowAuditLogsDrawer,
  selectedProject,
}: IProps) {
  const _exporter = useRef<ExcelExport>(null);
  const Pagination = getPaginationData();

  const [logs, setLogs] = useState<IAuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState<IPageState>(Pagination.initialDataState);
  const [pageSizeValue, setPageSizeValue] = useState<number | string | undefined>();
  const [sort, setSort] = useState<State["sort"]>([{ field: "performedOn", dir: "desc" }]);
  const [filter, setFilter] = useState<CompositeFilterDescriptor>({ logic: "and", filters: [] });
  const [dataState, setDataState] = useState<State>({ skip: 0, take: Pagination.initialDataState.take });

  const fetchLogs = (
    currentFilter: CompositeFilterDescriptor,
    currentSort: State["sort"],
    skip: number,
    take: number
  ) => {
    if (!selectedProject?.projectId) return;

    auditLogSvc
      .getProjectAuditHistoryPaged(selectedProject.projectId, currentFilter, currentSort, skip, take)
      .then((res) => {
        if (res.data) {
          setLogs(res.data);
          setTotal(res.total ?? 0);
        } else {
          setLogs([]);
          setTotal(0);
        }
      });
  };

  const excelExport = () => {
    if (!_exporter.current || !selectedProject?.projectId) return;
    auditLogSvc
      .getProjectAuditHistoryPaged(selectedProject.projectId, filter, sort, 0, 99999)
      .then((res) => {
        if (res.data) {
          const exportData = (res.data as IAuditLog[]).map((log) => ({
            ...log,
            performedOn: formatDateForExport(log.performedOn),
          }));
          _exporter.current?.save(exportData);
        }
      });
  };

  useEffect(() => {
    if (showAuditLogsDrawer && selectedProject?.projectId) {
      const initialFilter: CompositeFilterDescriptor = { logic: "and", filters: [] };
      const initialSort: State["sort"] = [{ field: "performedOn", dir: "desc" }];
      const { skip, take } = Pagination.initialDataState;
      setPage(Pagination.initialDataState);
      setDataState({ skip, take });
      setFilter(initialFilter);
      setSort(initialSort);
      setPageSizeValue(undefined);
      fetchLogs(initialFilter, initialSort, skip, take);
    } else {
      setLogs([]);
      setTotal(0);
    }
  }, [showAuditLogsDrawer, selectedProject?.projectId]);

  useEffect(() => {
    if (showAuditLogsDrawer) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }
    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [showAuditLogsDrawer]);

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    setDataState(event.dataState);
  };

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take = targetEvent.value === "All" ? total : event.page.take;
    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({ ...event.page, take });
    fetchLogs(filter, sort, event.page.skip, event.page.take);
  };

  const onSortChange = (event: GridSortChangeEvent) => {
    setSort(event.sort);
    fetchLogs(filter, event.sort, page.skip, page.take);
  };

  const onFilterChange = (event: GridFilterChangeEvent) => {
    const newFilter: CompositeFilterDescriptor = event.filter ?? { logic: "and", filters: [] };
    setFilter(newFilter);
    setPage(Pagination.initialDataState);
    fetchLogs(newFilter, sort, 0, page.take);
  };

  return createPortal(
    <Drawer
      expanded={showAuditLogsDrawer}
      position={"start"}
      mode={"overlay"}
      animation={{ duration: 400 }}
      className="customDrawer2 transition-all duration-500"
    >
      <DrawerContent>
        <div className="overlay" onClick={() => setShowAuditLogsDrawer(false)}></div>
        <div
          className={`max-w-[1280px] flex flex-col bg-neutral-10 px-4 fixed right-0 top-0 bottom-0 h-screen w-full transform transition-all duration-500 overflow-y-scroll ${
            showAuditLogsDrawer ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="py-4 flex items-center justify-between gap-2 border-b border-neutral-30">
            <h2 className="font-medium text-lg">
              Project: {selectedProject?.projectName || selectedProject?.text} Logs
            </h2>
            <div className="flex items-center gap-2">
              <div className="cursor-pointer" onClick={excelExport}>
                <Export />
                <ExcelExport
                  data={logs}
                  fileName={`${selectedProject?.projectName || selectedProject?.text || "Project"}-AuditLogs.xlsx`}
                  ref={_exporter}
                >
                  <ExcelExportColumn field="entityType" title="Entity" width={150} />
                  <ExcelExportColumn field="operationType" title="Action" width={150} />
                  <ExcelExportColumn field="performedOn" title="Action On" width={170} />
                  <ExcelExportColumn field="performedBy" title="Action By" width={170} />
                  <ExcelExportColumn field="levelName" title="Level" width={130} />
                  <ExcelExportColumn field="subLevelName" title="Sub Level" width={130} />
                  <ExcelExportColumn field="shopDrawingCode" title="Shop Drawing" width={350} />
                  <ExcelExportColumn field="description" title="Description" width={300} />
                </ExcelExport>
              </div>
              <Button
                fillMode="flat"
                className="p-2 bg-none outline-none"
                onClick={() => setShowAuditLogsDrawer(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className="flex-1 py-4">
            <Grid
              style={{ width: "100%", maxWidth: "100vw", height: "calc(100vh - 180px)" }}
              size="small"
              className="customHeight employee project-audit-logs"
              scrollable="scrollable"
              data={logs}
              {...dataState}
              onDataStateChange={dataStateChange}
              skip={page.skip}
              take={page.take}
              total={total}
              pageable={{
                buttonCount: 5,
                info: true,
                type: "numeric",
                pageSizes: [10, 20, 50, 100],
                previousNext: true,
                pageSizeValue: pageSizeValue,
              }}
              onPageChange={pageChange}
              sortable={true}
              sort={sort}
              onSortChange={onSortChange}
              filter={filter}
              onFilterChange={onFilterChange}
              resizable={true}
            >
              <GridColumn field="entityType" title="Entity" width={120} filter="text" columnMenu={ColumnMenu} />
              <GridColumn field="operationType" title="Action" width={120} filter="text" columnMenu={ColumnMenu} />
              <GridColumn
                field="performedOn"
                title="Action On"
                width={170}
                filter="date"
                columnMenu={ColumnMenu}
                cells={{
                  data: (props: any) => <DateCell {...props} />,
                }}
              />
              <GridColumn field="performedBy" title="Action By" width={170} filter="text" columnMenu={ColumnMenu} />
              <GridColumn field="levelName" title="Level" width={130} filter="text" columnMenu={ColumnMenu} />
              <GridColumn field="subLevelName" title="Sub Level" width={130} filter="text" columnMenu={ColumnMenu} />
              <GridColumn field="shopDrawingCode" title="Shop Drawing" width={200} filter="text" columnMenu={ColumnMenu} />
              <GridColumn field="description" title="Description" filter="text" columnMenu={ColumnMenu} />
            </Grid>
          </div>

          <div className="flex justify-end gap-2 pb-4 sticky-buttons">
            <Button
              className="border border-[#E2E8F0] px-4 py-2 font-medium"
              onClick={() => setShowAuditLogsDrawer(false)}
            >
              Close
            </Button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>,
    document.body
  );
}

export default ProjectAuditLogsDrawer;
